/**
 * Generates a temporary password for a staff account being created by an admin.
 *
 * Two things shape the alphabet. It has to satisfy the server's rules — a lowercase
 * letter, an uppercase letter, a digit and a special, and nothing outside the allowed
 * set — and it has to survive being read off a screen or dictated over a phone, so the
 * characters that get confused for each other are left out: no O/0, no I/l/1, and only
 * the specials that do not need explaining or escaping.
 */

const LOWERCASE = 'abcdefghijkmnpqrstuvwxyz'; // no l, no o
const UPPERCASE = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // no I, no O
const DIGITS = '23456789'; // no 0, no 1
/** A subset of the server's allowed specials, minus the quoting and shell hazards. */
const SPECIALS = '!@#$%^&*_+-=?.';

const ALL = LOWERCASE + UPPERCASE + DIGITS + SPECIALS;

/** Long enough that the reduced alphabet costs nothing, short enough to type by hand. */
export const GENERATED_PASSWORD_LENGTH = 16;

/**
 * Uniform over `0..bound-1`, rejecting the tail of the random range that would
 * otherwise make the low values more likely. `crypto` rather than `Math.random`:
 * this is a credential, not a placeholder.
 */
function randomIndex(bound: number): number {
  const limit = Math.floor(0x100000000 / bound) * bound;
  const buffer = new Uint32Array(1);
  let value = 0;
  do {
    crypto.getRandomValues(buffer);
    value = buffer[0];
  } while (value >= limit);
  return value % bound;
}

function pick(alphabet: string): string {
  return alphabet[randomIndex(alphabet.length)];
}

export function generateTemporaryPassword(
  length: number = GENERATED_PASSWORD_LENGTH
): string {
  // One from each class up front, so the result always satisfies the rules rather
  // than satisfying them by luck and occasionally being rejected.
  const characters = [
    pick(LOWERCASE),
    pick(UPPERCASE),
    pick(DIGITS),
    pick(SPECIALS),
  ];

  while (characters.length < length) {
    characters.push(pick(ALL));
  }

  // Fisher-Yates, so the guaranteed four are not always in the first four positions.
  for (let i = characters.length - 1; i > 0; i--) {
    const j = randomIndex(i + 1);
    [characters[i], characters[j]] = [characters[j], characters[i]];
  }

  return characters.join('');
}
